import * as angular from 'angular';
import * as _ from 'lodash';

import { IRequestService } from '../request.service';
import { ITemplateService } from '../../services/data/template.service';
import { IUploadService, IValueTypeHelperService } from '../../services/helpers';
import { Alarm, AlarmList } from '../../models/alarms/Alarm';
import { AlarmEmailDTO as AlarmEmailDto, ImportResultDto, InputValuesStatusDto } from '../../models/DTO';
import { AlarmLevel, AlarmRowViewModel, AlarmStoreSlice, IStringDateRange2, IValueViewModel, TagDateValue } from '../../models';
import { ValueTypes, AlarmModes } from '../../common';


export interface IAlarmService {
    getAll(force?: boolean): angular.IPromise<AlarmList>;
    getAllForTemplates(): angular.IPromise<AlarmList>;
    getList(identifiersGuid: string[]): angular.IPromise<AlarmList>;
    save(identifierGuid: string, alarms: Alarm[]): angular.IPromise<Alarm[]>;
    saveList(value: { [identifierGuid: string]: Alarm[] }): angular.IPromise<{ [identifierGuid: string]: Alarm[] }>;
    delete(identifierGuid: string, alarmGuid: string): angular.IPromise<void>;
    getPercentageBetweenDates(beginDate: string, endDate: string, force?: boolean): angular.IPromise<InputValuesStatusDto[]>;
    getEvolutionBetweenDates(beginDate: string, endDate: string, force?: boolean): angular.IPromise<TagDateValue[]>;
    /**
     * @deprecated Use events service 
     */
    getEmailAlarm(force?: boolean): angular.IPromise<AlarmEmailDto[]>;
    /**
    * @deprecated Use events service
    */
    addOrUpdateEmailAlarm(alarmEmailDto: AlarmEmailDto): angular.IPromise<AlarmEmailDto>;
    /**
    * @deprecated Use events service
    */
    deleteEmailAlarm(actionGuid: string, listenerGuid: string): angular.IPromise<void>;
    upload(file: File): angular.IPromise<ImportResultDto<Alarm>>;
    mapStoreToAlarmRowViewModels(store: AlarmStoreSlice): AlarmRowViewModel[];
}

export class AlarmService implements IAlarmService {
    static serviceId = 'alarmService';
    static serviceUrl = 'alarm/';

    static $inject = [
        'requestService',
        'templateService',
        'uploadService',
        'valueTypeHelperService'
    ];

    constructor(
        private _requestService: IRequestService,
        private _templateService: ITemplateService,
        private _uploadService: IUploadService,
        private _valueTypeHelper: IValueTypeHelperService
    ) {
    }

    private controllerUrl() {
        return `${this._requestService.getUrlV1()}${AlarmService.serviceUrl}`;
    }

    private getAtV1(url: string = '', args?: IStringDateRange2): angular.IPromise<any> {
        //this._requestService.cleanCacheAtV1();
        return this._requestService.getAtV1(AlarmService.serviceUrl + url, args, true);
    }

    private postAtV1(url: string = '', data?: any, args?: any): angular.IPromise<any> {
        return this._requestService.postAtV1(AlarmService.serviceUrl + url, data, args, true);
    }

    getAll(force?: boolean): angular.IPromise<AlarmList> {
        if (force) {
            this._requestService.cleanAllCache();
        }
        return this.getAtV1();
    }

    getAllForTemplates(): angular.IPromise<AlarmList> {
        return this.getAtV1('templates');
    }

    getList(identifiersGuid: string[]): angular.IPromise<AlarmList> {
        return this.postAtV1('', identifiersGuid);
    }

    save(identifierGuid: string, alarms: Alarm[]): angular.IPromise<Alarm[]> {
        return this.postAtV1(`save/${identifierGuid}`, alarms)
            .then(data => {
                this._templateService.cleanChangesCache();
                this._requestService.cleanCacheAtV1(AlarmService.serviceUrl);
                return data;
            });
    }

    saveList(value: { [identifierGuid: string]: Alarm[]; }): angular.IPromise<{ [identifierGuid: string]: Alarm[]; }> {
        return this.postAtV1(`saveList`, value)
            .then(data => {
                this._templateService.cleanChangesCache();
                this._requestService.cleanCacheAtV1(AlarmService.serviceUrl);
                return data;
            });
    }

    delete(identifierGuid: string, alarmGuid: string): angular.IPromise<void> {
        return this.getAtV1(`delete/${identifierGuid}/${alarmGuid}`)
            .then(data => {
                this._templateService.cleanChangesCache();
                this._requestService.cleanCacheAtV1(AlarmService.serviceUrl);
                return data;
            });
    }

    getPercentageBetweenDates(beginDate: string, endDate: string, force?: boolean): angular.IPromise<InputValuesStatusDto[]> {
        if (force) {
            this._requestService.cleanAllCache();
        }
        return this.getAtV1('getPercentageBetweenDates', { beginDate: beginDate, endDate: endDate });
    }

    getEvolutionBetweenDates(beginDate: string, endDate: string, force?: boolean): angular.IPromise<TagDateValue[]> {
        if (force) {
            this._requestService.cleanAllCache();
        }
        return this.getAtV1('getEvolutionBetweenDates', { beginDate: beginDate, endDate: endDate });
    }

    /**
     * @deprecated Use events service
     */
    getEmailAlarm(force: boolean = false): angular.IPromise<AlarmEmailDto[]> {
        const url = 'email';
        if (force) {
            this._requestService.cleanCacheAtV1(url);
        }
        return this.getAtV1(url);
    }

    /**
     * @deprecated Use events service
     */
    addOrUpdateEmailAlarm(alarmEmailDto: AlarmEmailDto): angular.IPromise<AlarmEmailDto> {
        const email = 'email';
        return this.postAtV1(email, alarmEmailDto)
            .then(data => {
                alarmEmailDto.guid = data.guid;
                alarmEmailDto.actionGuid = data.actionGuid;
                this._requestService.cleanCacheAtV1(AlarmService.serviceUrl + email);
                return data;
            });
    }

    /**
     * @deprecated Use events service
     */
    deleteEmailAlarm(actionGuid: string, listenerGuid: string): angular.IPromise<void> {
        return this.postAtV1(`email/delete/${actionGuid}/${listenerGuid}`);
    }

    upload(file: File): angular.IPromise<ImportResultDto<Alarm>> {
        const url = this.controllerUrl() + 'upload/';
        return this._uploadService
            .uploadFile<ImportResultDto<Alarm>>(url, file)
            .then(response => {
                this._requestService.cleanCacheAtV1('alarm/');
                this._requestService.cleanCacheAtV1('viewModel/guidList');
                this._requestService.cleanCacheAtV1('viewModel/all');
                return response;
            });
    }

    mapStoreToAlarmRowViewModels(store: AlarmStoreSlice): AlarmRowViewModel[] {
        const { alarmsDic, groupsDic, statesDic, valueViewModelsDic } = store;
        const valueViewModels = _.values<IValueViewModel>(valueViewModelsDic);
        return [].concat(...valueViewModels.filter(i => alarmsDic[i.guid])
            .map(i => {
                const group = groupsDic[i.groupList[0].guid];
                const states = group.groupStateCollection.map(gs => statesDic[gs.stateGuid])
                    .map(s => { return { key: s.guid, value: s.name }; });
                return alarmsDic[i.guid].map(a => {
                    return {
                        // angular.copy to get a deep copy; should be changed
                        alarm: angular.copy(a),
                        identifier: i,
                        viewModel: {
                            level: AlarmLevel[a.level],
                            valueType: ValueTypes.getValueType(a.value) || a.value,
                            mode: AlarmModes[a.mode].value,
                            states: a.states.map(s => statesDic[s]?.name),
                        },
                        editOptions: {
                            states: states,
                            valueTypes: this._valueTypeHelper.getIdentifierValueTypes(i.identifierType)
                        }
                    };
                });
            }));
    }
}